import { Component } from "@theme/component";
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    // GTM Marketing Tracking Event.
    const button = this.refs.addToCartButton;
    const action = button?.dataset.targetAtcAction;
    if (action === "atc") {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "add_to_cart",
      });
    }

    if (action === "buynow") {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "begin_checkout",
      });
    }

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest("product-form-component")
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== "true") {
      const animationEnabled = this.dataset.addToCartAnimation === "true";
      if (animationEnabled && !event.target.closest(".quick-add-modal")) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement("fly-to-cart"));

    let flyToCartClass = addToCartButton.classList.contains("quick-add__button")
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== "true") {
      addToCartButton.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /**
   * Tracks the sibling upsell block's state (if one exists in this section)
   * so the running total and the cart submission can both reflect it.
   * @type {{selected: boolean, quantity: number, variantId: string | null, price: number}}
   */
  #upsellState = { selected: false, quantity: 1, variantId: null, price: 0 };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // The upsell block (blocks/upsell-product.liquid) renders as a sibling
    // block in the same section rather than nested inside this component,
    // so we listen at the section level. The event bubbles, so this catches it.
    target?.addEventListener("upsell:change", this.#onUpsellChange, { signal });

    // Main product quantity changing should also refresh the running total.
    // Attached at the section level (not `this`) because the custom quantity
    // stepper (`settings.is_custom_input` variant) can render as a
    // section-level sibling rather than nested inside this component.
    target?.addEventListener("change", this.#onQuantityInputChange, { signal });

    // Read the upsell block's current state directly from the DOM on connect,
    // rather than waiting for its init `upsell:change` event. This avoids a
    // race: if the upsell block happens to sit earlier in the DOM than this
    // component, its init event could fire before this listener is attached.
    this.#syncUpsellStateFromDom(target);
    this.#updateFinalPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Reads the sibling upsell block's current selected/quantity/price state
   * directly from its DOM, if one is present in this section.
   * @param {Element | null} [scope] - Defaults to closest section/dialog/product-card.
   */
  #syncUpsellStateFromDom(scope) {
    const root = scope ?? this.closest(".shopify-section, dialog, product-card");
    const upsellBlock = /** @type {HTMLElement | null} */ (
      root?.querySelector("[data-upsell-block]")
    );
    if (!upsellBlock) return;

    const selectedInput = /** @type {HTMLInputElement | null} */ (
      upsellBlock.querySelector("[data-upsell-input-selected]")
    );
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      upsellBlock.querySelector("[data-upsell-input-quantity]")
    );

    this.#upsellState = {
      selected: selectedInput?.value === "true",
      quantity: Number(quantityInput?.value) || 1,
      variantId: upsellBlock.dataset.variantId ?? null,
      price: Number(upsellBlock.dataset.price) || 0,
    };
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString(),
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector("quantity-selector-component")
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === "product-form-component"
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the upsell block being toggled or its quantity changing.
   * @param {CustomEvent<{productId: string, variantId: string, price: number, quantity: number, selected: boolean}>} event
   */
  #onUpsellChange = (event) => {
    this.#upsellState = {
      selected: event.detail.selected,
      quantity: event.detail.quantity,
      variantId: event.detail.variantId,
      price: event.detail.price,
    };
    this.#updateFinalPrice();
  };

  /**
   * Handles the main product's quantity input changing, to keep the total live.
   * @param {Event} event
   */
  #onQuantityInputChange = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target?.name !== "quantity") return;
    this.#updateFinalPrice();
  };

  /**
   * Resolves the current main-product quantity, checking (in order):
   *  1. The API-based quantity-selector-component, if rendered.
   *  2. A `name="quantity"` input inside this component/form.
   *  3. A section-level sibling custom stepper (`settings.is_custom_input`
   *     variant of the quantity snippet), which does not render nested
   *     inside `<product-form-component>`.
   * Always returns a finite number >= 1 - never NaN.
   * @returns {number}
   */
  #getMainQuantity() {
    const apiQty = this.refs.quantitySelector?.getValue?.();
    if (Number.isFinite(apiQty) && apiQty > 0) return apiQty;

    const inFormInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="quantity"]')
    );
    if (inFormInput) {
      const value = Number(inFormInput.value);
      if (Number.isFinite(value) && value > 0) return value;
    }

    const scope = this.closest(".shopify-section, dialog, product-card");
    const siblingInput = /** @type {HTMLInputElement | null} */ (
      scope?.querySelector("[data-qty-hidden-input]")
    );
    if (siblingInput) {
      const value = Number(siblingInput.value);
      if (Number.isFinite(value) && value > 0) return value;
    }

    return 1;
  }

  /**
   * Recomputes and broadcasts the running total (main product + upsell, if selected)
   * for any `final-price` block on the page to pick up.
   */
  #updateFinalPrice = () => {
    const mainPrice = Number(this.dataset.variantPrice) || 0;
    const mainQty = this.#getMainQuantity();

    let total = mainPrice * mainQty;

    if (this.#upsellState.selected) {
      total += this.#upsellState.price * this.#upsellState.quantity;
    }

    document.dispatchEvent(new CustomEvent("finalprice:update", { detail: { amount: total } }));
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();
    // A child button with data-to-checkout="true" adds to cart, then redirects
    // straight to checkout instead of opening the cart drawer.
    const submitter = /** @type {HTMLButtonElement | null} */ (
      /** @type {SubmitEvent} */ (event).submitter
    );
    const goToCheckout = submitter?.dataset.toCheckout === "true";

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll("add-to-cart-component")
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled,
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace(
          "{{ maximum }}",
          validation.maxQuantity?.toString() || "",
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    // The quantity input may live outside the actual <form> (see
    // `#getMainQuantity`), so force the authoritative value into formData
    // here - otherwise a sibling custom stepper's quantity is silently
    // ignored and the cart always adds the form's own default.
    formData.set("quantity", String(this.#getMainQuantity()));

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    // Re-read the upsell state right before submit in case the shopper
    // toggled it without dispatching (defensive; normally kept in sync via
    // #onUpsellChange).
    this.#syncUpsellStateFromDom();

    // Build the main line item, preserving any properties[...] fields already in the form.
    /** @type {{id: number, quantity: number, properties?: Record<string, FormDataEntryValue>}} */
    const mainItem = {
      id: Number(formData.get("id")),
      quantity: Number(formData.get("quantity")) || 1,
    };
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[")) {
        mainItem.properties = mainItem.properties || {};
        mainItem.properties[key.slice(11, -1)] = value;
      }
    }

    const items = [mainItem];
    if (this.#upsellState.selected && this.#upsellState.variantId) {
      items.push({
        id: Number(this.#upsellState.variantId),
        quantity: this.#upsellState.quantity,
      });
    }

    // Gift-with-purchase: tag main item + gift item with a shared bundle id
    // so the cart can identify/lock/cascade-delete the pair.
    const giftVariantId = this.dataset.giftVariantId;
    if (giftVariantId) {
      const bundleId = crypto.randomUUID();
      mainItem.properties = { ...(mainItem.properties || {}), _bundle_id: bundleId };
      items.push({
        id: Number(giftVariantId),
        quantity: Number(formData.get("quantity")),
        properties: { _bundle_id: bundleId, _is_gift: "true" },
      });
    }

    console.log(
      "mainItem.id:",
      mainItem.id,
      "giftVariantId:",
      giftVariantId,
      "items:",
      JSON.parse(JSON.stringify(items)),
    );

    const hasMultipleItems = items.length > 1;
    const cfg = fetchConfig("javascript", { body: formData });

    const fetchPromise = hasMultipleItems
      ? fetch(Theme.routes.cart_add_url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "text/html" },
          body: JSON.stringify({ items, sections: formData.get("sections") || undefined }),
        })
      : fetch(Theme.routes.cart_add_url, {
          ...cfg,
          headers: { ...cfg.headers, Accept: "text/html" },
        });

    fetchPromise
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector(".add-to-cart-text--added");
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              goingToCheckout: goToCheckout,
            }),
          );
          if (goToCheckout) {
            // window.location.href = `${Theme.routes.root}checkout`;
            window.location.href = "/checkout";
            return;
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        "beforebegin",
        /** @type {Element} */ (newElement.cloneNode(true)),
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? "";

    // Keep the running total in sync with the newly selected variant's price.
    if (event.detail.resource?.price != null) {
      this.dataset.variantPrice = String(event.detail.resource.price);
    }
    this.#updateFinalPrice();

    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]',
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
      );
    }

    const newQuantityRules = event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector("price-per-item");
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector(".product-form-buttons");

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]',
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback,
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector("volume-pricing");
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons,
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
